import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
} from '@mui/icons-material';
import { useGetAppVersionQuery, useUpdateAppVersionMutation } from '../../store/api/adminApi';

interface PlatformVersionInfo {
  version: string;
  buildNumber: number;
  minVersion: string;
  minBuildNumber: number;
}

interface AppVersionData {
  android: PlatformVersionInfo;
  ios: PlatformVersionInfo;
  forceUpdate: boolean;
  updateMessage: string;
  createdAt: string | null;
  updatedAt: string | null;
}

const defaultPlatformVersion: PlatformVersionInfo = {
  version: '1.0.0',
  buildNumber: 1,
  minVersion: '1.0.0',
  minBuildNumber: 1,
};

const defaultAppVersion: AppVersionData = {
  android: { ...defaultPlatformVersion },
  ios: { ...defaultPlatformVersion },
  forceUpdate: false,
  updateMessage: '',
  createdAt: null,
  updatedAt: null,
};

const AppVersionTab: React.FC = () => {
  const [versionData, setVersionData] = useState<AppVersionData>(defaultAppVersion);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useGetAppVersionQuery();
  const [updateAppVersion, { isLoading: saving }] = useUpdateAppVersionMutation();

  useEffect(() => {
    if (data) {
      setVersionData({
        android: data.android || defaultPlatformVersion,
        ios: data.ios || defaultPlatformVersion,
        forceUpdate: data.forceUpdate || false,
        updateMessage: data.updateMessage || '',
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null,
      });
    }
  }, [data]);

  const saveVersionData = async () => {
    try {
      setSaveError(null);

      await updateAppVersion({
        android: versionData.android,
        ios: versionData.ios,
        forceUpdate: versionData.forceUpdate,
        updateMessage: versionData.updateMessage,
      }).unwrap();

      setSnackbarMessage('バージョン情報を保存しました');
      setSnackbarOpen(true);

      refetch();
    } catch (err: any) {
      console.error('バージョン情報の保存に失敗しました:', err);
      setSaveError(err.data?.error || 'バージョン情報の保存に失敗しました');
    }
  };

  const handlePlatformChange = (
    platform: 'android' | 'ios',
    field: keyof PlatformVersionInfo,
    value: string | number
  ) => {
    setVersionData((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value,
      },
    }));
  };

  const isValidVersion = (version: string): boolean => {
    return /^\d+\.\d+\.\d+$/.test(version);
  };

  const canSave = (): boolean => {
    return (
      isValidVersion(versionData.android.version) &&
      isValidVersion(versionData.android.minVersion) &&
      isValidVersion(versionData.ios.version) &&
      isValidVersion(versionData.ios.minVersion) &&
      versionData.android.buildNumber > 0 &&
      versionData.android.minBuildNumber > 0 &&
      versionData.ios.buildNumber > 0 &&
      versionData.ios.minBuildNumber > 0
    );
  };

  const formatDate = (isoString: string | null): string => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString('ja-JP');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const displayError = saveError || (error ? 'バージョン情報の取得に失敗しました' : null);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">アプリバージョン管理</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{ mr: 1 }}
          >
            再読み込み
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveVersionData}
            disabled={saving || !canSave()}
          >
            {saving ? '保存中...' : '保存'}
          </Button>
        </Box>
      </Box>

      {displayError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {displayError}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>バージョン設定について:</strong><br />
          • <strong>最新バージョン</strong>: アプリストアでリリースされている最新バージョン<br />
          • <strong>最小必須バージョン</strong>: この値より古いバージョンのユーザーには強制アップデートが表示されます<br />
          • <strong>強制アップデート</strong>: ONにすると全ユーザーにアップデートを強制します
        </Typography>
      </Alert>

      <Stack spacing={3}>
        {/* プラットフォーム設定（横並び） */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Android設定 */}
          <Paper sx={{ p: 3, flex: 1 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AndroidIcon sx={{ mr: 1, color: '#3DDC84' }} />
              <Typography variant="h6">Android</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最新バージョン"
                  value={versionData.android.version}
                  onChange={(e) => handlePlatformChange('android', 'version', e.target.value)}
                  fullWidth
                  placeholder="1.0.23"
                  error={!isValidVersion(versionData.android.version) && versionData.android.version !== ''}
                  helperText="例: 1.0.23"
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最新ビルド番号"
                  type="number"
                  value={versionData.android.buildNumber}
                  onChange={(e) => handlePlatformChange('android', 'buildNumber', parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最小必須バージョン"
                  value={versionData.android.minVersion}
                  onChange={(e) => handlePlatformChange('android', 'minVersion', e.target.value)}
                  fullWidth
                  placeholder="1.0.20"
                  error={!isValidVersion(versionData.android.minVersion) && versionData.android.minVersion !== ''}
                  helperText="例: 1.0.20"
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最小必須ビルド番号"
                  type="number"
                  value={versionData.android.minBuildNumber}
                  onChange={(e) => handlePlatformChange('android', 'minBuildNumber', parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Box>
            </Box>
          </Paper>

          {/* iOS設定 */}
          <Paper sx={{ p: 3, flex: 1 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AppleIcon sx={{ mr: 1 }} />
              <Typography variant="h6">iOS</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最新バージョン"
                  value={versionData.ios.version}
                  onChange={(e) => handlePlatformChange('ios', 'version', e.target.value)}
                  fullWidth
                  placeholder="1.0.23"
                  error={!isValidVersion(versionData.ios.version) && versionData.ios.version !== ''}
                  helperText="例: 1.0.23"
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最新ビルド番号"
                  type="number"
                  value={versionData.ios.buildNumber}
                  onChange={(e) => handlePlatformChange('ios', 'buildNumber', parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最小必須バージョン"
                  value={versionData.ios.minVersion}
                  onChange={(e) => handlePlatformChange('ios', 'minVersion', e.target.value)}
                  fullWidth
                  placeholder="1.0.20"
                  error={!isValidVersion(versionData.ios.minVersion) && versionData.ios.minVersion !== ''}
                  helperText="例: 1.0.20"
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <TextField
                  label="最小必須ビルド番号"
                  type="number"
                  value={versionData.ios.minBuildNumber}
                  onChange={(e) => handlePlatformChange('ios', 'minBuildNumber', parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* 共通設定 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>共通設定</Typography>
          <Divider sx={{ mb: 2 }} />
          
          <FormControlLabel
            control={
              <Switch
                checked={versionData.forceUpdate}
                onChange={(e) => setVersionData((prev) => ({ ...prev, forceUpdate: e.target.checked }))}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body1">強制アップデート</Typography>
                <Typography variant="caption" color="text.secondary">
                  ONにすると、最小必須バージョンより古いユーザーはアプリを使用できなくなります
                </Typography>
              </Box>
            }
          />
          
          <Box mt={3}>
            <TextField
              label="アップデートメッセージ"
              value={versionData.updateMessage}
              onChange={(e) => setVersionData((prev) => ({ ...prev, updateMessage: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="新機能と改善が含まれた最新版が利用可能です。"
              helperText="アップデート画面に表示されるメッセージ（オプション）"
            />
          </Box>
        </Paper>

        {/* 最終更新情報 */}
        {(versionData.createdAt || versionData.updatedAt) && (
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              {versionData.createdAt && (
                <span>作成日時: {formatDate(versionData.createdAt)}</span>
              )}
              {versionData.updatedAt && (
                <span style={{ marginLeft: versionData.createdAt ? 24 : 0 }}>
                  最終更新: {formatDate(versionData.updatedAt)}
                </span>
              )}
            </Typography>
          </Paper>
        )}
      </Stack>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AppVersionTab;
